import { forwardRef, useImperativeHandle, useRef } from "react";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/components/ui/cn";

interface PollOptionInputProps {
  value: string;
  onChangeText: (text: string) => void;
  onRemove?: () => void;
  showRemoveButton?: boolean;
  placeholder?: string;
  isAddButton?: boolean;
  onAddPress?: () => void;
  className?: string;
}

export interface PollOptionInputRef {
  focus: () => void;
}

/**
 * A single poll option input field with optional remove button.
 * Can also render as an "Add another option" button with dashed border.
 * Matches mobile PollOptionInput styling.
 */
const PollOptionInput = forwardRef<PollOptionInputRef, PollOptionInputProps>(
  (
    {
      value,
      onChangeText,
      onRemove,
      showRemoveButton = false,
      placeholder = "Option",
      isAddButton = false,
      onAddPress,
      className,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    if (isAddButton) {
      return (
        <button
          type="button"
          onClick={onAddPress}
          className={cn(
            "w-full rounded-xl border border-dashed border-border-tertiary",
            "px-3 py-3 text-left",
            "text-body-2 text-text-tertiary",
            "hover:bg-surface-1 transition-colors",
            className
          )}
        >
          Add another option
        </button>
      );
    }

    return (
      <div
        className={cn(
          "flex items-center gap-2",
          "rounded-xl border border-border-tertiary bg-surface-1",
          "px-3 py-3",
          className
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChangeText(e.target.value)}
          placeholder={placeholder}
          maxLength={100}
          className={cn(
            "flex-1 bg-transparent",
            "text-body-2 text-text-primary placeholder:text-text-tertiary",
            "focus:outline-none"
          )}
        />
        {showRemoveButton && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-text-tertiary hover:text-text-secondary transition-colors"
            aria-label="Remove option"
          >
            <Icon name="xmark" size={16} />
          </button>
        )}
      </div>
    );
  }
);

PollOptionInput.displayName = "PollOptionInput";

export default PollOptionInput;
